// External deps
import { useContext, useEffect, useState } from 'react';
import { Button, Loader } from 'ypf-pas-general-ui-components';
import { BsPlusCircle } from 'react-icons/bs';
import { useParams } from 'react-router-dom';
import { PropTypes } from 'prop-types';

// Internal deps
import LoaderContext from '../../../../../context/LoaderProvider';
import useToast from '../../../../../hooks/useToast';
import {
  deleteMonthlyApprovers,
  getMonthlyApprovers
} from '../../../../../services/monthlyServiceRequest';
import { groupApprovers } from '../../../../../utils/formatUtils';
import './MonthlyApproversTab.scss';
import { ApproversPermissions } from '../../../../../components/MonthlyServiceRequest/RequestDetailTypes';
import { DetailRequestMonthly } from '../../../../../utils/globalPropTypes';
import { statusEnum } from '../../../../../utils/dataStatus';

import MonthlyAddApproversForm from './MonthlyAddApproversForm/MonthlyAddApproversForm';
import MonthlyApproversTable from './MonthlyApproversTable/MonthlyApproversTable';

const ungroupApprovers = (pooledApprovers) => pooledApprovers?.flat();

const MonthlyApproversTab = ({ details, aprroverPermissions, idStatus }) => {
  const [pooledApprovers, setPooledApprovers] = useState([]);
  const { id } = useParams();
  const { setLoader } = useContext(LoaderContext);
  const [localLoader, setLocalLoader] = useState(false);
  const [showAddApproversForm, setShowAddApproversForm] = useState(false);
  const { setNewToast } = useToast();
  const canEdit = aprroverPermissions.write;

  const disabledEditStateCancelOrFinish =
    idStatus === statusEnum.cancelada || idStatus === statusEnum.finalizada;

  const fetchData = async (local) => {
    if (local) {
      setLocalLoader(true);
    } else {
      setLoader(true);
    }
    try {
      const data = await getMonthlyApprovers(id);
      const pooledData = groupApprovers(data);

      setPooledApprovers(pooledData);
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
    if (local) {
      setLocalLoader(false);
    } else {
      setLoader(false);
    }
  };

  const handleDelete = async (approverId) => {
    setLocalLoader(true);
    try {
      await deleteMonthlyApprovers(approverId);
      await fetchData(true);
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
    setLocalLoader(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="monthly-approvers-tab mb-2 position-relative">
      <div className="approvers-tab-header d-flex justify-content-between align-items-center">
        <h5 className="my-0">Listado de aprobadores</h5>
        <Button
          colorIcon="primary"
          disabled={!canEdit || disabledEditStateCancelOrFinish}
          fontSizeLabel="15px"
          icon={BsPlusCircle}
          iconRight
          label="Agregar aprobador"
          onClick={() => setShowAddApproversForm(true)}
          sizeIcon="15px"
          type="button"
          variant="light"
          widthButton="210px"
        />
      </div>
      <hr className="divide-line" />
      {showAddApproversForm && (
        <MonthlyAddApproversForm
          currentApprovers={ungroupApprovers(pooledApprovers)}
          details={details}
          fetchApprovers={fetchData}
          hideForm={() => setShowAddApproversForm(false)}
          setLocalLoader={setLocalLoader}
          show={showAddApproversForm}
        />
      )}
      {pooledApprovers?.map((approvers, i) => (
        <div key={approvers[0]?.id}>
          <MonthlyApproversTable
            approvers={approvers}
            canEdit={
              canEdit &&
              ungroupApprovers(pooledApprovers).length > 1 &&
              !disabledEditStateCancelOrFinish
            }
            key={approvers[0].nombre}
            onDelete={handleDelete}
          />
          {pooledApprovers.length - 1 !== i && <hr className="divide-line my-4" />}
        </div>
      ))}
      <Loader show={localLoader} text="CARGANDO" />
    </div>
  );
};

export default MonthlyApproversTab;

MonthlyApproversTab.propTypes = {
  aprroverPermissions: ApproversPermissions,
  details: DetailRequestMonthly.isRequired,
  idStatus: PropTypes.number.isRequired
};

MonthlyApproversTab.defaultProps = {
  aprroverPermissions: {
    visualize: false,
    write: false
  }
};
